/**
 * Hook TanStack Form para el dialog de auto-delegación AFIP (ORR-111).
 *
 * Patrón consistente con `src/components/clientes/wizard/useClienteForm.ts`:
 * - `useForm` de @tanstack/react-form
 * - `validators.onDynamic` con schema Zod, que corre al enviar y, después del primer intento, en cada cambio
 * - delega ejecución al `onSubmit` que recibe por options
 */
import { revalidateLogic, useForm } from '@tanstack/react-form'
import {
  autoDelegarAfipSchema,
  type AutoDelegarAfipFormValues,
} from './autoDelegarAfipSchema'

export interface UseAutoDelegarAfipFormOptions {
  defaultValues?: Partial<AutoDelegarAfipFormValues>
  onSubmit?: (values: AutoDelegarAfipFormValues) => void | Promise<void>
}

const DEFAULT_VALUES: AutoDelegarAfipFormValues = {
  pin: '',
  servicios: ['wsccomu', 'wsmtxca', 'sistema_registral', 'monotributo'],
  guardarClave: false,
  consentimientoUrl: null,
}

export function useAutoDelegarAfipForm(options: UseAutoDelegarAfipFormOptions = {}) {
  const { defaultValues, onSubmit } = options

  const form = useForm({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    } as AutoDelegarAfipFormValues,
    // Valida al enviar y, después del primer intento, en cada cambio, así un error entre campos se limpia al corregir el otro campo.
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: autoDelegarAfipSchema,
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value)
      }
    },
  })

  return { form }
}

export type UseAutoDelegarAfipFormReturn = ReturnType<typeof useAutoDelegarAfipForm>
export type AutoDelegarAfipForm = UseAutoDelegarAfipFormReturn['form']
